import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Row,
    Col,
    CardBody,
    Card,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Label,
    Input,
} from "reactstrap";
import { Tooltip } from "@mui/material";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import ContactUsService from "./ContactUs.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { getDisplayFileName, getFileUrl, openStoredFile } from "../../utils/fileUtils";

const STATUS_OPTIONS = ["Pending", "Seen", "In Progress", "Responded", "Completed", "Closed"];

const ContactUsList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const [pageLimit, setPageLimit] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const { authData } = PermissionAccess();

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState("Pending");
    const [statusSaving, setStatusSaving] = useState(false);

    useEffect(() => {
        i18n.changeLanguage(localStorage.getItem('language'));
        fetchMessages(1, pageLimit);
    }, [pageLimit]);

    function fetchMessages(
        page_index,
        page_limit,
        text = filterText,
        type = filterType,
        status = filterStatus
    ) {
        setLoading(true);
        var offset = (page_index - 1) * page_limit;
        if (offset < 0) offset = 0;

        ContactUsService.getContactUsMessages(authData.sessionID, offset, page_limit, type, status, text).then(
            (response) => {
                setLoading(false);
                if (response.data.message === "Unauthorized") {
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                } else {
                    setMessages(response.data.data || []);
                    setTotalRows(response.data.count?.count || 0);
                }
            },
            () => {
                setLoading(false);
                Alerts.swalErrorAlert(t("Server Error, Please try again later."));
            }
        );
    }

    const formatTypeLabel = (type) => {
        if (!type) return "-";
        return String(type).charAt(0).toUpperCase() + String(type).slice(1);
    };

    const getTypeBadgeClass = (type) => {
        const normalized = String(type || "").toLowerCase();
        if (normalized === "patient") return "bg-primary";
        if (normalized === "pharmacy") return "bg-success";
        if (normalized === "doctor" || normalized === "nurse") return "bg-info";
        return "bg-secondary";
    };

    const getStatusBadgeClass = (status) => {
        const normalized = String(status || "Pending").toLowerCase();
        if (normalized === "pending") return "bg-warning text-dark";
        if (normalized === "seen") return "bg-info";
        if (normalized === "in progress") return "bg-primary";
        if (normalized === "responded" || normalized === "completed") return "bg-success";
        if (normalized === "closed") return "bg-secondary";
        return "bg-secondary";
    };

    const truncateText = (value, max = 40) => {
        const text = String(value || "").trim();
        if (!text) return "-";
        if (text.length <= max) return text;
        return `${text.slice(0, max)}...`;
    };

    const getAttachmentFileType = (attachment) => {
        const mime = String(attachment?.mimetype || "").toLowerCase();
        const name = String(attachment?.originalname || attachment?.filename || attachment?.path || "").toLowerCase();
        if (mime.includes("pdf") || name.endsWith(".pdf")) return "pdf";
        if (mime.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return "pdf";
        return "image";
    };

    const shortAttachmentLabel = (attachment, index) => {
        const fullName = getDisplayFileName(attachment, `File ${index + 1}`);
        const extensionMatch = fullName.match(/(\.[a-z0-9]+)$/i);
        const extension = extensionMatch ? extensionMatch[1] : "";
        const baseName = extension ? fullName.slice(0, -extension.length) : fullName;

        if (baseName.length <= 18) return fullName;
        return `${baseName.slice(0, 12)}...${extension}`;
    };

    const openViewModal = (row) => {
        setSelectedMessage(row);
        setSelectedStatus(row.status || "Pending");
        setViewModalOpen(true);
    };

    const openStatusModal = (row) => {
        setSelectedMessage(row);
        setSelectedStatus(row.status || "Pending");
        setStatusModalOpen(true);
    };

    const closeViewModal = () => {
        setViewModalOpen(false);
        if (!statusModalOpen) {
            setSelectedMessage(null);
            setSelectedStatus("Pending");
        }
        setStatusSaving(false);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        if (!viewModalOpen) {
            setSelectedMessage(null);
            setSelectedStatus("Pending");
        }
        setStatusSaving(false);
    };

    const applyStatusLocally = (id, status) => {
        setMessages((prev) =>
            prev.map((item) => (item._id === id ? { ...item, status } : item))
        );
        setSelectedMessage((prev) => (prev && prev._id === id ? { ...prev, status } : prev));
    };

    const handleSaveStatus = (closeAfterSave = true) => {
        if (!selectedMessage?._id) return;

        setStatusSaving(true);
        ContactUsService.updateContactUsStatus(
            authData.sessionID,
            selectedMessage._id,
            selectedStatus
        ).then(
            (response) => {
                setStatusSaving(false);
                if (response.data.message === "Unauthorized") {
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                    return;
                }
                if (response.data?.status) {
                    applyStatusLocally(selectedMessage._id, selectedStatus);
                    Alerts.swalSuccessAlert(t("Status updated successfully."));
                    if (closeAfterSave) {
                        closeStatusModal();
                    }
                } else {
                    Alerts.swalErrorAlert(
                        response.data?.message || t("Unable to update status. Please try again.")
                    );
                }
            },
            () => {
                setStatusSaving(false);
                Alerts.swalErrorAlert(t("Server Error, Please try again later."));
            }
        );
    };

    const columns = [
        {
            name: t('Date'),
            selector: row => new Date(row.datetime_added).toLocaleString(),
            width: "18%",
            sortable: true,
        },
        {
            name: t('Name'),
            selector: row => row.name,
            width: "16%",
            sortable: true,
            cell: (row) => (
                <span title={row.name || ""}>{truncateText(row.name, 28)}</span>
            ),
        },
        {
            name: t('Type'),
            selector: row => row.type,
            width: "12%",
            sortable: true,
            cell: (row) => (
                <span className={`badge ${getTypeBadgeClass(row.type)}`}>
                    {formatTypeLabel(row.type)}
                </span>
            )
        },
        {
            name: t('Subject'),
            selector: row => row.subject,
            width: "22%",
            sortable: true,
            cell: (row) => (
                <Tooltip
                    placement="top"
                    arrow
                    title={
                        <div style={{ whiteSpace: "pre-wrap", maxWidth: 340, fontSize: 13, lineHeight: 1.45 }}>
                            {row.message || t("No message")}
                        </div>
                    }
                >
                    <span style={{ cursor: "help", borderBottom: "1px dotted #9ca3af" }}>
                        {truncateText(row.subject, 36)}
                    </span>
                </Tooltip>
            ),
        },
        {
            name: t('Status'),
            selector: row => row.status || "Pending",
            width: "14%",
            sortable: true,
            cell: (row) => (
                <span className={`badge ${getStatusBadgeClass(row.status)}`}>
                    {row.status || "Pending"}
                </span>
            ),
        },
        {
            name: t('Actions'),
            width: "18%",
            cell: (row) => (
                <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                    <Tooltip placement="top" title={`${t("View")} ${t("Details")}`} arrow>
                        <i
                            className="pe-7s-look text-primary btn-icon-wrapper fs-5"
                            style={{ cursor: "pointer" }}
                            onClick={() => openViewModal(row)}
                        />
                    </Tooltip>
                    <Tooltip placement="top" title={`${t("Update")} ${t("Status")}`} arrow>
                        <i
                            className="pe-7s-pen text-success btn-icon-wrapper fs-5"
                            style={{ cursor: "pointer" }}
                            onClick={() => openStatusModal(row)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    function handlePageChange(page_index) {
        setCurrentPage(page_index);
        fetchMessages(page_index, pageLimit);
    }

    async function handlePerRowsChange(newPageLimit) {
        setPageLimit(newPageLimit);
    }

    const handleSearch = (e) => {
        const text = e.target.value;
        setFilterText(text);
        setCurrentPage(1);
        fetchMessages(1, pageLimit, text, filterType, filterStatus);
    };

    const handleTypeFilter = (e) => {
        const type = e.target.value;
        setFilterType(type);
        setCurrentPage(1);
        fetchMessages(1, pageLimit, filterText, type, filterStatus);
    };

    const handleStatusFilter = (e) => {
        const status = e.target.value;
        setFilterStatus(status);
        setCurrentPage(1);
        fetchMessages(1, pageLimit, filterText, filterType, status);
    };

    const attachments = Array.isArray(selectedMessage?.attachments)
        ? selectedMessage.attachments
        : [];

    const detailFieldStyle = {
        background: "#f8fafc",
        border: "1px solid #eef2f7",
        borderRadius: "10px",
        padding: "12px 14px",
        minHeight: "64px",
    };

    const detailLabelStyle = {
        display: "block",
        fontSize: "12px",
        fontWeight: 600,
        color: "#6b7280",
        marginBottom: "6px",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
    };

    return (
        <Fragment>
            <div className="app-main__outer">
                <div className="app-main__inner">
                    <div className="app-page-title">
                        <div className="page-title-wrapper">
                            <div className="page-title-heading fw-bold">
                                <div className="page-title-icon">
                                    <i className="pe-7s-mail icon-gradient bg-tempting-azure" />
                                </div>
                                <div>
                                    {t("Contact Us Messages")}
                                    <div className="page-title-subheading">
                                        {t("View and manage inquiries from different apps")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Row>
                        <LoadingOverlay
                            tag="div"
                            active={loading}
                            spinner={<DNALoader />}
                        >
                            <Col md="12">
                                <Card className="main-card mb-3">
                                    <CardBody>
                                        <div className="d-flex mb-3 justify-content-between flex-wrap gap-2">
                                            <div className="d-flex flex-wrap gap-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("Search by name, email, subject...")}
                                                    value={filterText}
                                                    onChange={handleSearch}
                                                    style={{ width: '280px' }}
                                                />
                                                <select
                                                    className="form-select"
                                                    value={filterType}
                                                    onChange={handleTypeFilter}
                                                    style={{ width: '160px' }}
                                                >
                                                    <option value="">{t("All Types")}</option>
                                                    <option value="patient">{t("Patient")}</option>
                                                    <option value="doctor">{t("Doctor")}</option>
                                                    <option value="nurse">{t("Nurse")}</option>
                                                    <option value="pharmacy">{t("Pharmacy")}</option>
                                                </select>
                                                <select
                                                    className="form-select"
                                                    value={filterStatus}
                                                    onChange={handleStatusFilter}
                                                    style={{ width: '160px' }}
                                                >
                                                    <option value="">{t("All Statuses")}</option>
                                                    {STATUS_OPTIONS.map((status) => (
                                                        <option key={status} value={status}>
                                                            {t(status)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <DataTable
                                            data={messages}
                                            columns={columns}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationResetDefaultPage={resetPaginationToggle}
                                            fixedHeader
                                            fixedHeaderScrollHeight="500px"
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </LoadingOverlay>
                    </Row>
                </div>
            </div>

            <Modal isOpen={viewModalOpen} toggle={closeViewModal} size="lg" centered>
                <ModalHeader toggle={closeViewModal} className="border-0 pb-0">
                    <div>
                        <div className="fw-bold">{t("Contact Us Details")}</div>
                        <div className="text-muted" style={{ fontSize: "13px", fontWeight: 400 }}>
                            {t("Full inquiry details and attachments")}
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    {selectedMessage && (
                        <div>
                            <div
                                className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)",
                                    border: "1px solid #e5eefb",
                                    borderRadius: "12px",
                                    padding: "14px 16px",
                                }}
                            >
                                <div>
                                    <div className="fw-semibold" style={{ fontSize: "18px" }}>
                                        {selectedMessage.name || "-"}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: "13px" }}>
                                        {selectedMessage.email || "-"}
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${getTypeBadgeClass(selectedMessage.type)}`}>
                                        {formatTypeLabel(selectedMessage.type)}
                                    </span>
                                    <span className={`badge ${getStatusBadgeClass(selectedMessage.status)}`}>
                                        {selectedMessage.status || "Pending"}
                                    </span>
                                </div>
                            </div>

                            <Row className="g-3 mb-3">
                                <Col md="6">
                                    <div style={detailFieldStyle}>
                                        <span style={detailLabelStyle}>{t("Date")}</span>
                                        <div>
                                            {selectedMessage.datetime_added
                                                ? new Date(selectedMessage.datetime_added).toLocaleString()
                                                : "-"}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div style={detailFieldStyle}>
                                        <span style={detailLabelStyle}>{t("Subject")}</span>
                                        <div className="fw-semibold">{selectedMessage.subject || "-"}</div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="mb-3" style={detailFieldStyle}>
                                <span style={detailLabelStyle}>{t("Message")}</span>
                                <div
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {selectedMessage.message || "-"}
                                </div>
                            </div>

                            <div style={detailFieldStyle}>
                                <span style={detailLabelStyle}>{t("Attachments")}</span>
                                {attachments.length === 0 ? (
                                    <div className="text-muted">{t("No attachments")}</div>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                        {attachments.map((attachment, index) => {
                                            const fullLabel = getDisplayFileName(attachment, `File ${index + 1}`);
                                            const shortLabel = shortAttachmentLabel(attachment, index);
                                            const fileType = getAttachmentFileType(attachment);
                                            const href = getFileUrl(
                                                attachment.path || attachment.url || attachment.filename,
                                                { fileType }
                                            );
                                            return (
                                                <button
                                                    key={`${selectedMessage._id}-att-${index}`}
                                                    type="button"
                                                    className="btn btn-sm"
                                                    title={fullLabel}
                                                    disabled={!href}
                                                    onClick={() =>
                                                        openStoredFile(
                                                            attachment.path || attachment.url || attachment.filename,
                                                            fileType
                                                        )
                                                    }
                                                    style={{
                                                        background: "#fff",
                                                        border: "1px solid #dbe3ef",
                                                        borderRadius: "999px",
                                                        padding: "6px 12px",
                                                        color: "#2563eb",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <i className="pe-7s-paperclip me-1" />
                                                    {shortLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="light" onClick={closeViewModal}>
                        {t("Close")}
                    </Button>
                    <Button
                        color="success"
                        onClick={() => {
                            const row = selectedMessage;
                            setViewModalOpen(false);
                            if (row) openStatusModal(row);
                        }}
                    >
                        {t("Update Status")}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={statusModalOpen} toggle={closeStatusModal} centered>
                <ModalHeader toggle={closeStatusModal} className="border-0 pb-0">
                    <div>
                        <div className="fw-bold">{t("Update Status")}</div>
                        <div className="text-muted" style={{ fontSize: "13px", fontWeight: 400 }}>
                            {t("Mark this inquiry as seen, in progress, or completed")}
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    {selectedMessage && (
                        <div>
                            <div
                                className="mb-3"
                                style={{
                                    background: "#f8fafc",
                                    border: "1px solid #eef2f7",
                                    borderRadius: "12px",
                                    padding: "14px 16px",
                                }}
                            >
                                <div className="fw-semibold mb-1">{selectedMessage.name || "-"}</div>
                                <div className="text-muted mb-2" style={{ fontSize: "13px" }}>
                                    {selectedMessage.subject || "-"}
                                </div>
                                <span className={`badge ${getStatusBadgeClass(selectedMessage.status)}`}>
                                    {t("Current")}: {selectedMessage.status || "Pending"}
                                </span>
                            </div>

                            <Label className="fw-semibold mb-2" for="contactUsStatus">
                                {t("New Status")}
                            </Label>
                            <Input
                                id="contactUsStatus"
                                type="select"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                disabled={statusSaving}
                            >
                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>
                                        {t(status)}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="light" onClick={closeStatusModal} disabled={statusSaving}>
                        {t("Cancel")}
                    </Button>
                    <Button color="primary" onClick={() => handleSaveStatus(true)} disabled={statusSaving}>
                        {statusSaving ? t("Saving...") : t("Save Status")}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ContactUsList;
